/**
 * DASHBOARD METRIC SOURCE OF TRUTH.
 *
 * Every customer-facing dashboard number — the summary tiles, the review
 * table, the featured (Continue) card and the Recently Ready Reports list —
 * must read its status grouping and its recovery amount from THIS module.
 * No dashboard component may re-derive a status bucket or re-sum recoveries.
 *
 * This module intentionally contains NO business logic: recovery values come
 * straight from the canonical signals already mapped onto the Case by
 * `useMyReviewsAsCases` (`c.ai.potentialSavings`). The demo heuristic
 * (`buildReport`) is used only for unauthenticated sample cases that carry no
 * `ai` payload at all.
 */

import type { Case, CaseStatus } from "@/lib/cases";
import { buildReport, currency } from "@/lib/report";
import { FINANCIAL_PHRASES } from "@/lib/report/financial-vocabulary";

/* ───────────────────────── Status buckets ───────────────────────── */

/** Work still moving through the review pipeline. */
export const IN_PROGRESS_STATUSES: readonly CaseStatus[] = [
  "awaiting_payment",
  "received",
  "reviewing",
  "action_required",
  "awaiting_customer",
  "pipeline_blocked",
];

/** A findings report exists and is available to the customer. */
export const READY_STATUSES: readonly CaseStatus[] = ["report_ready", "self_serve"];

/**
 * Full-service negotiation states. `waiting_vendor` belongs here — the
 * negotiation has started and we are waiting on the vendor's response; it is
 * NOT a review-in-progress state.
 */
export const NEGOTIATION_STATUSES: readonly CaseStatus[] = [
  "negotiating",
  "waiting_vendor",
  "offer_received",
];

export const COMPLETED_STATUSES: readonly CaseStatus[] = ["completed"];

/** Statuses where a recovery figure is meaningful to the customer. */
export const RECOVERY_ELIGIBLE_STATUSES: readonly CaseStatus[] = [
  ...READY_STATUSES,
  ...NEGOTIATION_STATUSES,
  ...COMPLETED_STATUSES,
];

export function isInProgress(c: Case): boolean {
  return IN_PROGRESS_STATUSES.includes(c.status);
}
export function isReportReady(c: Case): boolean {
  return READY_STATUSES.includes(c.status);
}
export function isNegotiating(c: Case): boolean {
  return NEGOTIATION_STATUSES.includes(c.status);
}
export function isCompleted(c: Case): boolean {
  return COMPLETED_STATUSES.includes(c.status);
}

/** True when this case contributes to Total Supported Recovery. */
export function isRecoveryEligible(c: Case): boolean {
  return RECOVERY_ELIGIBLE_STATUSES.includes(c.status) || c.ai?.reportReady === true;
}

/* ───────────────────────── Recovery amount ───────────────────────── */

/**
 * The single canonical recovery amount for a case, in dollars.
 * Returns `null` when no recovery has been established yet (still processing,
 * or findings are absent) so callers can distinguish "unknown" from "$0".
 */
export function recoveryAmountFor(c: Case): number | null {
  const ai = c.ai;
  if (ai) {
    return typeof ai.potentialSavings === "number" ? Math.max(0, ai.potentialSavings) : null;
  }
  // Demo / unauthenticated sample case only.
  return buildReport(c).potentialSavings[1];
}

/** Sum of every recovery-eligible case's canonical recovery amount. */
export function totalSupportedRecovery(cases: Case[]): number {
  return cases
    .filter(isRecoveryEligible)
    .reduce((acc, c) => acc + (recoveryAmountFor(c) ?? 0), 0);
}

/**
 * True when a real recovery figure exists for this case. Cases that are still
 * processing, or whose invoice could not be read with sufficient confidence,
 * are excluded from the Total Supported Recovery tile so the tile never
 * implies a $0 outcome that has not actually been established.
 */
export function hasCalculableRecovery(c: Case): boolean {
  if (!isRecoveryEligible(c)) return false;
  const amount = recoveryAmountFor(c);
  if (amount === null) return false;
  const lowConfidence = typeof c.ai?.confidence === "number" && c.ai.confidence < 0.5;
  return !(amount === 0 && lowConfidence);
}

/** Recovery-eligible cases still awaiting a usable recovery figure. */
export function pendingRecoveryCount(cases: Case[]): number {
  return cases.filter((c) => isRecoveryEligible(c) && !hasCalculableRecovery(c)).length;
}

export interface DashboardTotals {
  inProgress: number;
  ready: number;
  negotiations: number;
  completed: number;
  totalSupportedRecovery: number;
  /** Reviews counted as ready/negotiating but with no usable recovery figure. */
  recoveryPending: number;
  /** Reviews that contributed a real figure to Total Supported Recovery. */
  recoveryCounted: number;
}

/** The four customer-facing summary tiles, computed once from one source. */
export function buildDashboardTotals(cases: Case[]): DashboardTotals {
  return {
    inProgress: cases.filter(isInProgress).length,
    ready: cases.filter(isReportReady).length,
    negotiations: cases.filter(isNegotiating).length,
    completed: cases.filter(isCompleted).length,
    totalSupportedRecovery: totalSupportedRecovery(cases.filter(hasCalculableRecovery)),
    recoveryPending: pendingRecoveryCount(cases),
    recoveryCounted: cases.filter(hasCalculableRecovery).length,
  };
}

/* ───────────────────────── Customer wording ───────────────────────── */

/**
 * PRESENTATION CONSTANTS. These change how values are worded, never how they
 * are computed.
 *
 * - `VALUE_NOT_AVAILABLE`  — the underlying value is NULL/unknown. Never $0.
 * - `VALUE_NOT_YET_AVAILABLE` — a total that no review has contributed to yet.
 * - `RECOVERY_PENDING_LABEL` — the ONE customer-facing phrase used everywhere
 *   a recovery figure cannot yet be stated (dashboard, featured card, table,
 *   mobile cards). Replaces "Unable to calculate" / "Could not calculate" /
 *   "In review" / "Requires review" / "—".
 */
export const VALUE_NOT_AVAILABLE = "Not Available";
export const VALUE_NOT_YET_AVAILABLE = "Not Yet Available";
export const RECOVERY_PENDING_LABEL = "Recovery calculation pending";
/** Label for a completed analysis whose supported recovery is a true $0. */
export const NO_SUPPORTED_RECOVERY_LABEL = "No supported recovery";
/** Note beneath a completed review with a true $0 recovery. */
export const COMPLETED_NO_RECOVERY_NOTE =
  "Review completed with no supported recovery identified";
/** Note beneath a positive recovery figure. */
export const SUPPORTED_RECOVERY_IDENTIFIED = "Supported recovery identified";

/** Currency for a value that may be unknown. NULL/undefined is never "$0". */
export function optionalCurrency(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value)
    ? currency(value)
    : VALUE_NOT_AVAILABLE;
}

/** Range for a value that may be unknown. NULL is never "$0–$0". */
export function optionalRange(range: readonly [number, number] | null | undefined): string {
  if (!range) return VALUE_NOT_AVAILABLE;
  const [low, high] = range;
  if (!Number.isFinite(low) || !Number.isFinite(high)) return VALUE_NOT_AVAILABLE;
  return `${currency(low)}–${currency(high)}`;
}

/**
 * The Total Supported Recovery tile. When no review has contributed a figure
 * the tile must not assert "$0" — that is a conclusion the data does not
 * support. The sum itself is unchanged.
 */
export function totalRecoveryTileDisplay(totals: {
  totalSupportedRecovery: number;
  recoveryCounted: number;
  recoveryPending: number;
}): { value: string; note?: string } {
  const { totalSupportedRecovery, recoveryCounted, recoveryPending } = totals;
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

  if (recoveryCounted === 0) {
    return {
      value: VALUE_NOT_YET_AVAILABLE,
      note:
        recoveryPending > 0
          ? `${plural(recoveryPending, "review")} awaiting recovery calculation`
          : undefined,
    };
  }
  return {
    value: currency(totalSupportedRecovery),
    note:
      recoveryPending > 0
        ? `${plural(recoveryCounted, "report")} included · ${recoveryPending} awaiting calculation`
        : `${plural(recoveryCounted, "report")} included`,
  };
}

export type RecoveryTone = "muted" | "savings";

export interface RecoveryDisplay {
  /** Column / metric label. */
  label: string;
  /** Primary rendered value (always the real figure when one exists). */
  value: string;
  /** Optional plain-language explanation shown beneath the value. */
  note?: string;
  tone: RecoveryTone;
}


/**
 * Customer-facing presentation of a case's recovery figure.
 * Legitimate $0 results are never hidden — they are shown as $0 with a plain
 * explanation. Technical states ("not extracted", "unavailable") are phrased
 * in professional, legally accurate language.
 */
export function recoveryDisplay(c: Case): RecoveryDisplay {
  const ai = c.ai;
  const amount = recoveryAmountFor(c);
  const lowConfidence = typeof ai?.confidence === "number" && ai.confidence < 0.5;
  const eligible = isRecoveryEligible(c);

  if (isCompleted(c)) {
    const val = amount ?? 0;
    return val > 0
      ? {
          label: "Recovery Achieved",
          value: currency(val),
          note: SUPPORTED_RECOVERY_IDENTIFIED,
          tone: "savings",
        }
      : {
          label: NO_SUPPORTED_RECOVERY_LABEL,
          value: currency(0),
          note: COMPLETED_NO_RECOVERY_NOTE,
          tone: "muted",
        };
  }

  if (eligible) {
    if (amount === null) {
      return {
        label: "Total Supported Recovery",
        value: RECOVERY_PENDING_LABEL,
        note: "Recovery figure is being finalized",
        tone: "muted",
      };
    }
    if (amount === 0 && lowConfidence) {
      return {
        label: "Total Supported Recovery",
        value: RECOVERY_PENDING_LABEL,
        note: "Invoice details could not be read with sufficient confidence",
        tone: "muted",
      };
    }
    if (amount === 0) {
      return {
        label: NO_SUPPORTED_RECOVERY_LABEL,
        value: currency(0),
        note: FINANCIAL_PHRASES.empty,
        tone: "muted",
      };
    }
    return {
      label: "Total Supported Recovery",
      value: currency(amount),
      note: SUPPORTED_RECOVERY_IDENTIFIED,
      tone: "savings",
    };
  }


  if (c.status === "action_required" || c.status === "awaiting_customer") {
    return {
      label: "Total Supported Recovery",
      value: RECOVERY_PENDING_LABEL,
      note: "Waiting for missing invoice pages",
      tone: "muted",
    };
  }
  if (c.status === "pipeline_blocked") {
    return {
      label: "Total Supported Recovery",
      value: RECOVERY_PENDING_LABEL,
      note: "Our team is resolving an issue with this submission",
      tone: "muted",
    };
  }
  if (c.status === "awaiting_payment") {
    return {
      label: "Total Supported Recovery",
      value: RECOVERY_PENDING_LABEL,
      note: "Your review begins after payment",
      tone: "muted",
    };
  }
  if (c.status === "received") {
    return {
      label: "Total Supported Recovery",
      value: RECOVERY_PENDING_LABEL,
      note: "Review not started",
      tone: "muted",
    };
  }
  return {
    label: "Total Supported Recovery",
    value: RECOVERY_PENDING_LABEL,
    note: "Recovery figure available once the review completes",
    tone: "muted",

  };
}

/** Professional placeholder when the vendor name could not be established. */
export const VENDOR_UNIDENTIFIED_LABEL = "Vendor requires review";

/** Vendor name for display, never a technical placeholder. */
export function vendorLabel(c: Case): { text: string; identified: boolean } {
  const v = c.details.vendor?.trim();
  return v ? { text: v, identified: true } : { text: VENDOR_UNIDENTIFIED_LABEL, identified: false };
}
